"use client";

import { useOrgHref } from "@/hooks/useOrgHref";
import { AmandaCard } from "./AmandaCards";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
}

function renderMarkdown(text: string) {
  // Simple markdown: **bold**, *italic*, `code`, [link](url), bullet lists
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-[10px] font-mono">$1</code>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-brand-600 underline underline-offset-2 hover:text-brand-700">$1</a>');

  // Line breaks
  html = html.replace(/\n/g, "<br />");

  return html;
}

export default function ChatMessage({ role, content, isStreaming }: ChatMessageProps) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[var(--color-gold)] px-3 py-2 text-xs text-[var(--color-ink)] leading-relaxed shadow-sm">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-slate-100 px-3 py-2 text-xs text-slate-700 leading-relaxed shadow-sm">
        <div
          className="chat-markdown [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:space-y-1 [&_p]:mb-1.5 [&_p:last-child]:mb-0"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
        />
        {isStreaming && (
          <span className="inline-flex items-center gap-1 ml-1">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-pulse" />
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: "0.15s" }} />
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: "0.3s" }} />
          </span>
        )}
      </div>
    </div>
  );
}

export function ActionCard({ action, data, isLive }: { action: string; data: Record<string, unknown>; isLive?: boolean }) {
  const orgHref = useOrgHref();
  if (action === "discount_offered") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border-2 border-[var(--color-gold)] bg-[var(--color-gold)]/5 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1.5">
            <svg className="h-4 w-4 text-[var(--color-gold-dark)]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
            </svg>
            <span className="text-xs font-semibold text-[var(--color-ink)]">Special Offer</span>
          </div>
          <p className="text-xs text-slate-700 mb-2">
            Use code <strong className="text-[var(--color-ink)] bg-[var(--color-gold)]/20 px-1.5 py-0.5 rounded font-mono text-[10px]">{data.code as string}</strong> at checkout for{" "}
            <strong>{data.discount_percent as number}% off</strong>{" "}
            {(data.code as string)?.startsWith("ANNUAL") ? "your annual" : "your first month of"}{" "}
            {(data.target_plan as string)?.charAt(0).toUpperCase() + (data.target_plan as string)?.slice(1)}!
          </p>
          <a
            href={orgHref("/dashboard/billing")}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-3.5 py-2 text-xs font-semibold text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-gold-light)] transition-colors mb-1.5"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
            </svg>
            Go to Checkout — Apply Code
          </a>
          <p className="text-[11px] text-slate-500">Enter the code on the checkout page. Cancel anytime with one click.</p>
        </div>
      </div>
    );
  }

  if (action === "checkout_link") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%]">
          <a
            href={data.url as string}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Complete Upgrade — {(data.plan as string)?.charAt(0).toUpperCase() + (data.plan as string)?.slice(1)}
          </a>
        </div>
      </div>
    );
  }

  if (action === "escalated") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-medium text-blue-800">Your request has been sent to our support team</span>
          </div>
          <p className="text-xs text-blue-600 mt-1">We&apos;ll get back to you within 24 hours.</p>
        </div>
      </div>
    );
  }

  // Amanda V2 typed cards (prepared_action / draft_change / error). This
  // replaced the old "Confirm Action" card, which POSTed to a route that no
  // longer exists: Amanda prepares, the human completes it in the product.
  if (action === "card") {
    return <AmandaCard data={data} isLive={isLive} />;
  }

  if (action === "document_generated") {
    return <DocumentGeneratedCard envelopeId={data.envelope_id as string} title={data.title as string} />;
  }

  if (action === "document_revised") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border-2 border-indigo-200 bg-indigo-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            <span className="text-xs font-semibold text-indigo-800">Document updated to v{data.version as number}</span>
          </div>
          {(data.envelope_id as string) && (
            <a
              href={orgHref(`/envelopes/${data.envelope_id as string}/edit`)}
              className="inline-flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800 font-medium mt-1"
            >
              Open Editor
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          )}
        </div>
      </div>
    );
  }

  if (action === "trial_granted") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-semibold text-emerald-800">{(data.plan as string || "Pro").charAt(0).toUpperCase() + (data.plan as string || "pro").slice(1)} Trial Activated!</span>
          </div>
          <p className="text-xs text-emerald-700">Your 7-day free trial is active. Expires {data.expires_at ? new Date(data.expires_at as string).toLocaleDateString() : "in 7 days"}.</p>
        </div>
      </div>
    );
  }

  if (action === "envelope_setup_progress") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-2">
            {isLive ? (
              <svg className="h-4 w-4 text-slate-400 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
              </svg>
            ) : (
              <svg className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
            <span className="text-xs text-slate-600">
              {isLive ? "Setting up" : "Created"} &ldquo;{data.title as string || "Draft"}&rdquo;{isLive ? "..." : ""}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (action === "envelope_ready") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl border-2 border-brand-200 bg-brand-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-semibold text-brand-800">Envelope Ready</span>
          </div>
          <p className="text-xs text-slate-700 mb-2">&ldquo;{data.title as string || "Document"}&rdquo; is set up with recipients and fields.</p>
          <a
            href={orgHref(`/envelopes/${data.envelope_id}?source=amanda`)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Review &amp; Send
          </a>
        </div>
      </div>
    );
  }

  if (action === "envelope_created") {
    // Legacy backward compat — render same as envelope_ready
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%]">
          <a
            href={orgHref(`/envelopes/${data.envelope_id}?source=amanda`)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            Open Envelope — {data.title as string || "Draft"}
          </a>
        </div>
      </div>
    );
  }

  return null;
}



function DocumentGeneratedCard({ envelopeId, title }: { envelopeId: string; title: string }) {
  const orgHref = useOrgHref();
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl border-2 border-brand-200 bg-brand-50 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span className="text-xs font-semibold text-brand-800">Document Generated</span>
        </div>
        <p className="text-xs text-slate-700 mb-2">{title}</p>
        <div className="flex items-center gap-2">
          <a
            href={orgHref(`/envelopes/${envelopeId}/edit`)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Document
          </a>
          <a
            href={orgHref(`/envelopes/${envelopeId}`)}
            className="text-[10px] text-slate-500 hover:text-slate-700 font-medium"
          >
            Skip to fields
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * The dots, optionally captioned with what Amanda is doing RIGHT NOW.
 *
 * V2 buffers the whole turn's prose behind the honesty rail, so between "send"
 * and the rail clearing the answer there is nothing on screen at all. The
 * per-tool `status` event fills that silence with something true.
 */
export function TypingIndicator({ label }: { label?: string | null }) {
  return (
    <div className="flex justify-start">
      <div className="rounded-2xl rounded-bl-md bg-white border border-slate-100 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
          {label ? <span className="text-[11px] text-slate-500">{label}</span> : null}
        </div>
      </div>
    </div>
  );
}
